import React from 'react';

interface Filters {
  department: string;
  designation: string;
  nominationCapacity: string;
  reviewStatus?: string; // Optional - review status (Completed/Pending)
  okrStatus?: string; // Optional - only used in NominationApprovalPageView
}

interface FiltersModalProps {
  show: boolean;
  tempFilters: Filters;
  uniqueDepartments: string[];
  uniqueDesignations: string[];
  uniqueNominationCapacities: string[];
  uniqueReviewStatuses?: string[]; // Optional - review statuses (Completed/Pending)
  uniqueOkrStatuses?: string[]; // Optional - only provided by NominationApprovalPageView
  onChange: (filterType: keyof Filters, value: string) => void;
  onApply: () => void;
  onRemoveAll: () => void;
  onClose: () => void;
  arrowDropDownIcon?: string;
  isNominationReviewPage?: boolean; // Flag to determine if it's the 360 Feedback page
  isTeamReviewPage?: boolean; // Flag to determine if it's the Team Review page
  isHODReviewPage?: boolean; // Flag to determine if it's the HOD Review page
}

const FiltersModal: React.FC<FiltersModalProps> = ({
  show,
  tempFilters,
  uniqueDepartments,
  uniqueDesignations,
  uniqueNominationCapacities,
  uniqueReviewStatuses,
  uniqueOkrStatuses,
  onChange,
  onApply,
  onRemoveAll,
  onClose,
  arrowDropDownIcon,
  isNominationReviewPage = false,
  isTeamReviewPage = false,
  isHODReviewPage = false,
}) => {
  if (!show) return null;

  return (
    <>
      <div
        className="fixed inset-0 bg-gray-500 bg-opacity-50 z-40 mt-0"
        onClick={onClose}
      />

      <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4">
  <div className="max-h-[calc(100dvh-1.5rem)] w-full max-w-xl overflow-y-auto rounded-lg border border-gray-200 bg-white p-4 shadow-2xl sm:p-5">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-base font-semibold text-gray-800">Filters</h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 p-2 rounded-full transition-colors"
              aria-label="Close filters"
            >
              <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="grid grid-cols-1 gap-5 mb-8">
            {/* Department Filter */}
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <label className="text-sm text-gray-700 sm:w-48 sm:shrink-0">Select Department</label>
              <div className="relative flex-1">
                <select
                  value={tempFilters.department}
                  onChange={(e) => onChange('department', e.target.value)}
                  className="text-sm w-full appearance-none border border-gray-300 rounded-lg px-4 py-1 bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all duration-200 cursor-pointer hover:border-gray-400"
                >
                  <option value="">All Departments</option>
                  {uniqueDepartments.map(dept => (
                    <option key={dept} value={dept}>{dept}</option>
                  ))}
                </select>
                <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                  {arrowDropDownIcon ? <img src={arrowDropDownIcon} alt="Dropdown" className="w-5 h-5" /> : null}
                </div>
              </div>
            </div>

            {/* Designation Filter */}
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <label className="text-sm text-gray-700 sm:w-48 sm:shrink-0">Select Designation</label>
              <div className="relative flex-1">
                <select
                  value={tempFilters.designation}
                  onChange={(e) => onChange('designation', e.target.value)}
                  className="text-sm w-full appearance-none border border-gray-300 rounded-lg px-4 py-1 bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all duration-200 cursor-pointer hover:border-gray-400"
                >
                  <option value="">All Designations</option>
                  {uniqueDesignations.map(d => (
                    <option key={d} value={d}>{d}</option>
                  ))}
                </select>
                <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                  {arrowDropDownIcon ? <img src={arrowDropDownIcon} alt="Dropdown" className="w-5 h-5" /> : null}
                </div>
              </div>
            </div>

            {/* Nomination Capacity Filter */}
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <label className="text-sm text-gray-700 sm:w-48 sm:shrink-0">
                {isNominationReviewPage 
                  ? 'Select Nomination As' 
                  : isTeamReviewPage
                    ? 'Select GW 360 Feedback Status'
                    : isHODReviewPage
                      ? 'Select Status'
                      : 'Select GW 360 Feedback Status'}
              </label>
              <div className="relative flex-1">
                <select
                  value={tempFilters.nominationCapacity}
                  onChange={(e) => onChange('nominationCapacity', e.target.value)}
                  className="text-sm w-full appearance-none border border-gray-300 rounded-lg px-4 py-1 bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all duration-200 cursor-pointer hover:border-gray-400"
                >
                  <option value="">All</option>
                  {uniqueNominationCapacities.map(cap => (
                    <option key={cap} value={cap}>{cap}</option>
                  ))}
                </select>
                <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                  {arrowDropDownIcon ? <img src={arrowDropDownIcon} alt="Dropdown" className="w-5 h-5" /> : null}
                </div>
              </div>
            </div>

            {/* Review Status Filter - Only show when uniqueReviewStatuses is provided */}
            {uniqueReviewStatuses && uniqueReviewStatuses.length > 0 && (
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                <label className="text-sm text-gray-700 sm:w-48 sm:shrink-0">Select Status</label>
                <div className="relative flex-1">
                  <select
                    value={tempFilters.reviewStatus || ''}
                    onChange={(e) => onChange('reviewStatus', e.target.value)}
                    className="text-sm w-full appearance-none border border-gray-300 rounded-lg px-4 py-1 bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all duration-200 cursor-pointer hover:border-gray-400"
                  >
                    <option value="">All</option>
                    {uniqueReviewStatuses.map(status => (
                      <option key={status} value={status}>{status}</option>
                    ))}
                  </select>
                  <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                    {arrowDropDownIcon ? <img src={arrowDropDownIcon} alt="Dropdown" className="w-5 h-5" /> : null}
                  </div>
                </div>
              </div>
            )}

            {/* OKR Status Filter - Show when uniqueOkrStatuses is provided (even if empty) */}
            {uniqueOkrStatuses && (
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                <label className="text-sm text-gray-700 sm:w-48 sm:shrink-0">Select OKR Status</label>
                <div className="relative flex-1">
                  <select
                    value={tempFilters.okrStatus || ''}
                    onChange={(e) => onChange('okrStatus', e.target.value)}
                    className="text-sm w-full appearance-none border border-gray-300 rounded-lg px-4 py-1 bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all duration-200 cursor-pointer hover:border-gray-400"
                  >
                    <option value="">All OKR Statuses</option>
                    {uniqueOkrStatuses.map(status => (
                      <option key={status} value={status}>{status}</option>
                    ))}
                  </select>
                  <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                    {arrowDropDownIcon ? <img src={arrowDropDownIcon} alt="Dropdown" className="w-5 h-5" /> : null}
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <button
              onClick={onRemoveAll}
              className="w-full px-4 py-3 text-sm text-red-600 bg-white focus:outline-none transition-all duration-200 sm:w-auto sm:min-w-[150px]"
            >
              Remove All Filters
            </button>
            <button
              onClick={onApply}
              className="w-full px-4 py-3 text-sm text-purple-800 focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all duration-200 sm:w-auto sm:min-w-[120px]"
            >
              Apply Filters
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default FiltersModal;
